/**
 * Currency Vault container (C21, B4 — D02 §5): binds `CurrencyVault` (packages/ui, CurrencyVaultView.tsx) to the read
 * model (`GET /currencies`, `GET /currencies/operations`) and posts through the gateway (`useGateway`: preview → PIN →
 * commit, D-018, D-232). Every money figure is the server's (ADR-08 §2.1 item 2): the equivalent and the expected profit
 * come from a debounced dry run (`derived.total_iqd` / `total_usd`, `profit_iqd` / `profit_usd`), the "insufficient"
 * alerts from the dry run's `BALANCE_INSUFFICIENT` verdicts, the sale cost is pre-filled with the server's weighted
 * average (D-065, D-216) or its USD form (D-067 (3)), the rate field with the approved quote (D-126). The PIN dialog
 * names what it confirms: the old loss message (`:2111-2113`), a changed cost (D-065), the D-068 operation, otherwise
 * the old confirmation sentence (`:2101-2105`, built but unused in the old screen; every post now needs a PIN, D-018).
 * Display-only arithmetic here: the whole-unit breakdown remainder and the per-unit difference of the profit box
 * (BigInt over decimal strings), the bill-counter total and the ranking's percentage bar. Customer mode posts on an
 * existing customer account (D-017) or opens one inside the operation after the same-name check (C22: D-018,
 * `newAccounts`); an on-account part beyond the balance opens the old excess dialogs (R-16 / R-17, C22 D-218: one
 * operation with the engine's `surplus` option, every check kept, D-014). Corrections (delete / undo) are D06
 * reversal entries and get their button with the journal screen (V-16).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CurrencyVault, formatWithCommas,
  type CvCounterparty, type CvCustomerOption, type CvDirectType, type CvForm, type CvOpType, type CvPreview, type CvRow, type CvSettlement, type CvTypeFilter,
} from "@faylasuf/ui";
import { PRINT_ROOT_CLASS, StatementA4, printDocument } from "@faylasuf/print";
import type { CurrencyCardData, CurrencyOperationRow, CurrencyOperationsQuery, PreviewResult } from "../api/types.js";
import { useApp, useSession } from "../app/context.js";
import { engineErrors, errorMessage, isSessionLost } from "../app/errors.js";
import { useGateway } from "../app/operations.js";
import { keys } from "../app/query.js";
import { useToast } from "../app/toast.js";
import { useAccounts } from "./WalletsPage.js";
import { customerPinMessage, surplusPrimary, useExcessFlow, useNewCustomerName, type CustomerTarget, type ExcessResolution } from "../app/customer-picker.js";
import { officeDate } from "./wallets/StatementContainer.js";
import { usePrintOffice } from "../app/printOffice.js";
import { usePrintSettings } from "../app/documents.js";

const EMPTY: CvForm = {
  opType: "buy",
  directType: "deposit",
  counterparty: "box",
  customerName: "",
  customerId: "",
  newCustomerName: "",
  allocation: "debt",
  settlement: "IQD",
  receivedCash: "",
  remainingOnAccount: "",
  dueDate: "",
  amount: "",
  rate: "",
  costRate: "",
  note: "",
};

const positive = (s: string): boolean => /^\d*\.?\d+$/.test(s) && /[1-9]/.test(s);
const abs = (s: string): string => s.replace(/^-/, "");
const SCALE = 6;
/** Decimal string → BigInt at 10^-6 (display arithmetic only; posting figures are the server's). */
function toScaled(s: string): bigint {
  const neg = s.startsWith("-");
  const [i = "0", f = ""] = abs(s).split(".");
  const v = BigInt(i || "0") * 10n ** BigInt(SCALE) + BigInt((f + "000000").slice(0, SCALE) || "0");
  return neg ? -v : v;
}
function fromScaled(v: bigint): string {
  const neg = v < 0n;
  const a = neg ? -v : v;
  const base = 10n ** BigInt(SCALE);
  const frac = (a % base).toString().padStart(SCALE, "0").replace(/0+$/, "");
  return `${neg ? "-" : ""}${(a / base).toString()}${frac ? `.${frac}` : ""}`;
}
const decSub = (a: string, b: string): string => fromScaled(toScaled(a) - toScaled(b));
const decClampSub = (a: string, b: string): string => {
  const r = toScaled(a) - toScaled(b);
  return fromScaled(r < 0n ? 0n : r);
};
/** Old cards print "+" before the profit (`:2350`, `:3607`, `:3644`); a loss keeps its own "-" (F-244). */
const plusSigned = (s: string): string => (s.startsWith("-") ? `-${formatWithCommas(abs(s))}` : `+${formatWithCommas(s)}`);
const signed = (s: string): string => (s.startsWith("-") ? `-${formatWithCommas(abs(s))}` : s === "0" ? "0" : `+${formatWithCommas(s)}`);

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function CurrenciesPage() {
  const { t, source, locale, now } = useApp();
  const { session, hasPermission, sessionLost } = useSession();
  const printOffice = usePrintOffice(); // [C29] F-286: identity lines in the A4 header
  const printSettings = usePrintSettings(); // [C36] D-148 hide profit (T-17)
  const toast = useToast();
  const gateway = useGateway();
  const qc = useQueryClient();
  const tz = session.office.timeZone;
  const ar = locale === "ar";

  const [activeCode, setActiveCode] = useState("");
  const [form, setForm] = useState<CvForm>(EMPTY);
  const [typedRemaining, setTypedRemaining] = useState(false);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewRefusal, setPreviewRefusal] = useState<ReturnType<typeof engineErrors>>([]);
  const [busy, setBusy] = useState(false);
  const [showBillCounter, setShowBillCounter] = useState(false);
  const [billCounts, setBillCounts] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState<CvTypeFilter>("all");
  const [printRows, setPrintRows] = useState<readonly CurrencyOperationRow[] | null>(null);
  const seq = useRef(0);

  const summaryQuery = useQuery({ queryKey: keys.currencies, queryFn: () => source.currencies() });
  const feedQ: CurrencyOperationsQuery = {
    ...(currencyFilter !== "all" ? { currency: currencyFilter } : {}),
    ...(typeFilter !== "all" ? { type: typeFilter } : {}),
    ...(startDate ? { from: startDate } : {}),
    ...(endDate ? { to: endDate } : {}),
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
    limit: 500,
  };
  const feedQuery = useQuery({ queryKey: keys.currencyOperations(feedQ), queryFn: () => source.currencyOperations(feedQ) });
  const accountsQuery = useAccounts(hasPermission("debts") || hasPermission("view_debts_and_trusts"));
  for (const e of [summaryQuery.error, feedQuery.error]) if (e && isSessionLost(e)) sessionLost();
  const accounts = accountsQuery.data ?? [];
  const names = useNewCustomerName(accounts);
  /** [C22] the new / same-name-resolved customer of the open form (reset when the customer fields change). */
  const target = useRef<CustomerTarget | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(h);
  }, [search]);

  const summary = summaryQuery.data;
  const all = summary?.currencies ?? [];
  const visible = all.filter((c) => c.visible);
  const nameOf = (c: Pick<CurrencyCardData, "nameAr" | "nameEn">) => (ar ? c.nameAr : c.nameEn);
  // Old: the first currency is selected on arrival (`:110`).
  useEffect(() => {
    if (!activeCode && visible[0]) setActiveCode(visible[0].code);
  }, [visible.length]);
  const cur = all.find((c) => c.code === activeCode) ?? visible[0] ?? null;
  const usdAvailable = !!summary?.usdVault && !summary.usdHidden;
  const usdSale = form.opType === "sell" && form.settlement === "USD" && usdAvailable;

  const prefillFor = (c: CurrencyCardData | null, op: CvOpType, settlement: CvSettlement): Partial<CvForm> => {
    if (!c) return {};
    if (op === "buy") return { rate: c.rates?.buy ?? "", costRate: "" };
    if (op === "sell") return settlement === "USD" ? { rate: "", costRate: c.costPrefillUsd ?? "" } : { rate: c.rates?.sell ?? "", costRate: c.costPrefill ?? "" };
    return { rate: "", costRate: "" };
  };
  // Old :306-325: a new currency re-fills the rates and clears the amount and the bill counter.
  useEffect(() => {
    if (!cur) return;
    setForm((f) => ({ ...f, amount: "", ...prefillFor(cur, f.opType, f.settlement) }));
    setBillCounts({});
    setShowBillCounter(false);
  }, [cur?.code, cur?.rates?.buy, cur?.rates?.sell]);
  // after a commit the average may move: refresh an empty cost field of an open sale
  useEffect(() => {
    if (form.opType === "sell" && form.costRate === "" && cur) setForm((f) => ({ ...f, ...prefillFor(cur, "sell", f.settlement), rate: f.rate || prefillFor(cur, "sell", f.settlement).rate || "" }));
  }, [cur?.costPrefill, cur?.costPrefillUsd]);

  const patch = (p: Partial<CvForm>) => {
    if ("customerName" in p || "customerId" in p || "newCustomerName" in p || "allocation" in p || "counterparty" in p || "settlement" in p) target.current = null;
    setForm((f) => ({ ...f, ...p }));
  };
  // Old :2567-2569: changing the type clears the form (rates re-filled by the type).
  const onOpTypeChange = (op: CvOpType) => {
    setTypedRemaining(false);
    setBillCounts({});
    setForm((f) => ({
      ...EMPTY,
      opType: op,
      directType: op === "direct" ? (directTypes[0] ?? "deposit") : f.directType,
      counterparty: op === "direct" ? "box" : f.counterparty,
      allocation: f.allocation,
      settlement: op === "sell" ? f.settlement : "IQD",
      ...prefillFor(cur, op, op === "sell" ? f.settlement : "IQD"),
    }));
  };
  const onCounterpartyChange = (cp: CvCounterparty) => patch(cp === "box" ? { counterparty: cp, dueDate: "" } : { counterparty: cp });
  const onSettlementChange = (s: CvSettlement) => {
    if (s === "USD" && !usdAvailable) return;
    target.current = null;
    setForm((f) => ({ ...f, settlement: s, customerId: "", customerName: "", receivedCash: "", remainingOnAccount: "", ...prefillFor(cur, f.opType, s) }));
  };

  const directTypes: CvDirectType[] = [
    ...(hasPermission("currency_opening_balance") ? (["opening"] as const) : []),
    ...(hasPermission("currency_direct_deposit_withdraw") ? (["deposit", "withdraw", "writeoff"] as const) : []),
  ];

  // ---- the operation the form describes (null = incomplete) ----
  const type =
    form.opType === "direct"
      ? form.directType === "opening" ? "currency_opening_balance" : form.directType === "deposit" ? "currency_direct_deposit" : form.directType === "withdraw" ? "currency_direct_withdraw" : "currency_writeoff"
      : form.opType === "buy" ? "currency_buy" : usdSale ? "currency_sell_for_usd" : "currency_sell";
  const counterparty = (accountId: string, surplus?: ExcessResolution["surplus"]): Record<string, unknown> =>
    form.counterparty === "box"
      ? { kind: "cash" }
      : {
          kind: "customer",
          accountId,
          ...(surplus ? { surplus } : {}),
          ...(form.receivedCash !== "" ? { cashReceived: form.receivedCash } : typedRemaining && form.remainingOnAccount !== "" ? { remainingOnAccount: form.remainingOnAccount } : {}),
          ...(form.dueDate ? { dueDate: form.dueDate } : {}),
        };
  const addNewLabel = t("cv.add_new_account");
  /** A customer typed but not picked (old: created silently, R-15; now the same-name check and D-018). */
  const typedCustomerName = form.customerName === addNewLabel ? form.newCustomerName.trim() : form.customerName.trim();
  const operationInput = (accountId: string = form.customerId, surplus?: ExcessResolution["surplus"]): Record<string, unknown> | null => {
    if (!cur || !positive(form.amount)) return null;
    const note = form.note ? { note: form.note } : {};
    if (form.opType === "direct") {
      const needsCost = form.directType === "opening" || form.directType === "deposit";
      if (needsCost && !positive(form.costRate)) return null;
      return { currency: cur.code, qty: form.amount, ...(needsCost ? { costRate: form.costRate } : {}), ...(form.directType !== "opening" ? { reason: form.note.trim() } : {}) };
    }
    if (!positive(form.rate)) return null;
    if (form.counterparty === "customer" && !accountId) return null;
    const cp = counterparty(accountId, surplus);
    if (form.opType === "buy") {
      if (!summary?.iqdVault) return null;
      return { currency: cur.code, qty: form.amount, rate: form.rate, counterparty: cp, iqdVaultAccountId: summary.iqdVault.id, ...note };
    }
    if (usdSale) return { currency: cur.code, qty: form.amount, usdRate: form.rate, ...(form.costRate ? { costRateUsd: form.costRate } : {}), counterparty: cp, usdVaultAccountId: summary!.usdVault!.id, ...note };
    if (!summary?.iqdVault) return null;
    return { currency: cur.code, qty: form.amount, rate: form.rate, ...(form.costRate ? { costRate: form.costRate } : {}), counterparty: cp, iqdVaultAccountId: summary.iqdVault.id, ...note };
  };
  // The dry run of the boxes: an unpicked customer is planned as cash (the equivalent and the profit do not depend on it).
  const unpicked = form.opType !== "direct" && form.counterparty === "customer" && !form.customerId && typedCustomerName !== "";
  const baseInput = operationInput(unpicked ? "unpicked" : form.customerId);
  const input = baseInput && unpicked ? { ...baseInput, counterparty: { kind: "cash" } } : baseInput;
  const inputKey = JSON.stringify([type, input]);

  // Debounced dry run: the boxes and alerts show only what the server planned.
  useEffect(() => {
    setPreview(null);
    setPreviewRefusal([]);
    if (!input) return;
    const my = ++seq.current;
    const h = setTimeout(async () => {
      try {
        const pv = await gateway.preview(type, input);
        if (my === seq.current) setPreview(pv);
      } catch (e) {
        if (my !== seq.current) return;
        if (isSessionLost(e)) sessionLost();
        else setPreviewRefusal(engineErrors(e)); // shown as the old live alerts; the rest surfaces on submit
      }
    }, 300);
    return () => clearTimeout(h);
  }, [inputKey]);

  const total = preview?.derived[usdSale ? "total_usd" : "total_iqd"];
  const profit = preview?.derived[usdSale ? "profit_usd" : "profit_iqd"];
  const unitDiff = form.opType === "sell" && positive(form.rate) && positive(form.costRate) ? decSub(form.rate, form.costRate) : "0";
  const previewBox: CvPreview | null =
    total !== undefined && form.opType !== "direct"
      ? {
          equivalentLabel: formatWithCommas(total),
          equivalentRaw: total,
          ...(form.opType === "sell" && profit !== undefined
            ? {
                profit: {
                  label: signed(profit),
                  diffLabel: signed(unitDiff),
                  absLabel: formatWithCommas(abs(profit)),
                  tone: profit.startsWith("-") ? ("negative" as const) : profit === "0" ? ("zero" as const) : ("positive" as const),
                },
              }
            : {}),
        }
      : null;

  // live alerts from the dry run's verdicts (old :3002-3088 computed in the browser)
  const shortOn = (pred: (account: string) => boolean) => previewRefusal.find((e) => e.code === "BALANCE_INSUFFICIENT" && pred(String(e.params["account"] ?? "")));
  const stockErr = cur?.stock ? shortOn((a) => a === cur.stock!.accountId) : undefined;
  const cashErr = form.opType === "buy" && form.counterparty === "box" ? shortOn((a) => a !== cur?.stock?.accountId) : undefined;

  // Old breakdown sync (:265-289, :2890-2925) over the server total.
  useEffect(() => {
    if (form.counterparty !== "customer") return;
    if (!total) return setForm((f) => (f.receivedCash === "" && f.remainingOnAccount === "" ? f : { ...f, receivedCash: "", remainingOnAccount: "" }));
    if (form.receivedCash === "" && !typedRemaining) setForm((f) => ({ ...f, remainingOnAccount: total }));
  }, [total, form.counterparty]);
  const onCashReceivedChange = (v: string) => {
    setTypedRemaining(false);
    patch({ receivedCash: v, remainingOnAccount: total ? decClampSub(total, v || "0") : "" });
  };
  const onRemainingChange = (v: string) => {
    setTypedRemaining(true);
    patch({ remainingOnAccount: v, receivedCash: total ? decClampSub(total, v || "0") : "" });
  };

  // ---- customers (existing accounts in the settlement currency; a new one is opened inside the operation, C22) ----
  const settleCur = usdSale ? "USD" : "IQD";
  const customerOptions: CvCustomerOption[] = accounts
    .filter((a) => a.kind === (form.allocation === "debt" ? "customer_debt" : "customer_trust") && a.currency === settleCur && !a.archived)
    .map((a) => ({ id: a.id, name: a.name ?? "", amountLabel: `${formatWithCommas(a.balance)} ${settleCur === "USD" ? "$" : t("cv.iqd")} (${form.allocation === "debt" ? t("cv.debt_one") : t("cv.trust_one")})` }));
  const onCustomerPick = (o: CvCustomerOption | "new") => {
    if (o === "new") patch({ customerName: addNewLabel, customerId: "" });
    else patch({ customerName: o.name, customerId: o.id });
  };

  // Old excess dialogs (CurrencyVaultView.tsx:3668-3724): titles and labels of the D02 call sites.
  const excess = useExcessFlow({
    accounts,
    onReduce: (balance) => {
      onRemainingChange(balance); // old :608-630: on account = the trust balance, the rest cash
      toast(t("excess.reduced_cv"), "info");
    },
    onResolved: (res) => void submit(res),
  });

  // ---- bill counter (old :549-564; display arithmetic over decimal strings) ----
  const onBillCountChange = (bill: string, count: string) => {
    const next = { ...billCounts, [bill]: count };
    setBillCounts(next);
    let sum = 0n;
    for (const [b, c] of Object.entries(next)) if (/^\d+$/.test(c.trim())) sum += toScaled(b) * BigInt(c.trim());
    patch({ amount: sum > 0n ? fromScaled(sum) : "" });
  };

  const canSubmit = input !== null && (form.opType === "direct" || form.counterparty !== "customer" || form.customerId !== "" || typedCustomerName !== "") && (form.opType !== "sell" || form.costRate !== "") && (form.opType !== "direct" || form.directType === "opening" || form.note.trim() !== "");

  const submit = async (excessRes?: ExcessResolution) => {
    if (!input || !cur) return;
    const customerMode = form.opType !== "direct" && form.counterparty === "customer";
    if (customerMode && !form.customerId && !target.current) {
      const resolved = await names.resolve(typedCustomerName, form.allocation, settleCur, "c");
      if (!resolved) return;
      target.current = resolved;
    }
    const opInput = customerMode ? operationInput(form.customerId || target.current?.accountId || "", excessRes?.surplus) : input;
    if (!opInput) return;
    const newAccounts = [...(customerMode && !form.customerId ? (target.current?.newAccounts ?? []) : []), ...(excessRes?.newAccounts ?? [])];
    setBusy(true);
    let pv: PreviewResult;
    try {
      pv = await gateway.previewWithNew(type, opInput, newAccounts);
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else {
        const sur = engineErrors(e).find((x) => x.code === "SURPLUS_ACCOUNT_REQUIRED");
        const primary = sur ? surplusPrimary(accounts, String(sur.params["account"] ?? ""), target.current, form.allocation, settleCur) : undefined;
        if (sur && primary && !excessRes) {
          excess.open({
            account: primary,
            surplus: String(sur.params["surplus"] ?? "0"),
            ...(primary.kind === "customer_trust" ? { title: t("excess.cv.trust_title"), actionTypeLabel: t("excess.trust.required") } : { actionTypeLabel: t("excess.debt.action") }),
          });
        } else toast(errorMessage(t, e), "error");
      }
      setBusy(false);
      return;
    }
    try {
      const amount = `${formatWithCommas(form.amount)} ${cur.symbol}`;
      const prefilled = usdSale ? pv.derived["prefilled_cost_rate_usd"] : pv.derived["prefilled_cost_rate"];
      const p = pv.derived[usdSale ? "profit_usd" : "profit_iqd"] ?? "0";
      let message: string;
      if (form.opType === "sell" && p.startsWith("-")) message = t("pin.message_currency_loss", { loss: formatWithCommas(abs(p)), unit: usdSale ? "$" : t("cv.iqd") });
      else if (form.opType === "sell" && prefilled !== form.costRate) message = t("pin.message_currency_cost_override", { prefilled: prefilled || "-", cost: form.costRate });
      else if (form.opType === "direct") message = t(`pin.message_currency_${form.directType}`, { amount });
      else {
        const opLabel = t(form.opType === "buy" ? "cv.op_buy" : "cv.op_sell");
        const customer = form.counterparty === "customer" ? t("cv.pin_for_customer", { name: form.customerId ? form.customerName : (target.current?.label ?? typedCustomerName) }) : "";
        message = t("pin.message_currency_trade", { op: opLabel, customer, amount });
      }
      const opened = [...(customerMode && !form.customerId && target.current?.newAccount ? [target.current.newAccount] : []), ...(excessRes?.newAccount ? [excessRes.newAccount] : [])];
      message = customerPinMessage(t, message, { newAccounts: opened, ...(excessRes ? { excess: { amount: pv.derived[usdSale ? "surplus_usd" : "surplus_iqd"] ?? "0", currency: usdSale ? "$" : t("cv.iqd"), target: excessRes.targetLabel } } : {}) });
      const res = await gateway.commitPreview(pv, { pinMessage: message });
      if (res) {
        target.current = null;
        // Old clearForm (:566-579): amount, note, due date, customer and counter cleared; rates kept.
        setForm((f) => ({ ...EMPTY, opType: f.opType, directType: f.directType, counterparty: f.counterparty, allocation: f.allocation, settlement: f.settlement, rate: f.rate, costRate: f.opType === "sell" ? "" : f.costRate }));
        setTypedRemaining(false);
        setBillCounts({});
        setShowBillCounter(false);
        void qc.invalidateQueries({ queryKey: keys.currencies });
      }
    } catch {
      /* toasted by the gateway */
    } finally {
      setBusy(false);
    }
  };

  // ---- inline rate edit (old :345-376 → PUT /rates, settings_currencies + PIN, D-126) ----
  const saveRates = async (buy: string, sell: string): Promise<boolean> => {
    if (!cur) return false;
    if (!positive(buy) || !positive(sell)) {
      toast(t("cv.rate_invalid"), "error");
      return false;
    }
    try {
      const r = await gateway.pin.withPin((proof) => source.setRate(cur.code, buy, sell, proof), { message: t("pin.message_currency_rates", { name: nameOf(cur), buy: formatWithCommas(buy), sell: formatWithCommas(sell) }), allowSupervisor: true });
      if (r === undefined) return false;
      toast(t("toast.rates_updated"), "success");
      if (D_SELL_LE_BUY(buy, sell)) toast(t("cv.sell_le_buy_warning"), "info"); // D-021: warn, allow
      void qc.invalidateQueries({ queryKey: keys.currencies });
      void qc.invalidateQueries({ queryKey: keys.rates });
      return true;
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else toast(errorMessage(t, e), "error");
      return false;
    }
  };

  // ---- ledger ----
  const fmtDateTime = useMemo(() => new Intl.DateTimeFormat(ar ? "ar-EG" : "en-US", { timeZone: tz, dateStyle: "short", timeStyle: "medium" }), [ar, tz]);
  const ops = feedQuery.data?.rows ?? [];
  const byCode = new Map(all.map((c) => [c.code, c]));
  const rows: CvRow[] = ops.map((r) => {
    const c = byCode.get(r.currency);
    return {
      id: r.operationId,
      kind: r.kind,
      currencyName: c ? nameOf(c) : r.currency,
      code: r.currency,
      symbol: c?.symbol ?? "",
      dateTimeLabel: fmtDateTime.format(new Date(r.postedAt)),
      operator: r.actorUsername ?? "-",
      qtyLabel: formatWithCommas(r.qty),
      profit: r.kind === "sell" ? { label: signed(r.profitIqd), positive: !r.profitIqd.startsWith("-") } : null,
      costLabel: r.kind === "sell" && r.costRate ? `${formatWithCommas(r.costRate)}${r.settlement === "USD" ? " $" : ""}` : null,
      equivalentLabel: r.equivalentIqd !== "0" ? formatWithCommas(r.equivalentIqd) : null,
      note: r.note,
    };
  });
  const today = officeDate(now(), tz);

  // p04: the currency statement (old :2224-2315) on the A4 template — output escaped by React (R-25 fixed), signed profit total.
  const print = () => {
    if (ops.length === 0) return toast(t("cv.nothing_to_print"), "info");
    setPrintRows(ops);
  };
  useEffect(() => {
    if (!printRows) return;
    const done = () => setPrintRows(null);
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [printRows]);

  // ---- ranking (old :3612-3617: percentage share for the bar, display only) ----
  const totalProfit = summary?.profits.totalIqd ?? "0";
  const ranking =
    summary?.profits.ranking?.map((x, i) => {
      const c = byCode.get(x.code);
      const pct = Number(totalProfit) > 0 ? Math.round((Number(x.profitIqd) / Number(totalProfit)) * 100) : 0;
      return { code: x.code, name: c ? nameOf(c) : x.code, profitLabel: plusSigned(x.profitIqd), sellCount: x.sellCount, pctShare: Math.max(0, pct), isTop: i === 0 && Number(x.profitIqd) > 0 };
    }) ?? null;

  const selected = cur
    ? {
        code: cur.code,
        name: nameOf(cur),
        symbol: cur.symbol,
        suspended: cur.suspended,
        buyRateLabel: formatWithCommas(cur.rates?.buy ?? "0"),
        sellRateLabel: formatWithCommas(cur.rates?.sell ?? "0"),
        quoteUnitLabel: formatWithCommas(cur.quoteUnit),
        multipleLabel: formatWithCommas(cur.multiple),
        bills: cur.bills.map((b) => ({ value: b, label: formatWithCommas(b) })),
        availableLabel: formatWithCommas(cur.stock?.available ?? "0"),
      }
    : null;

  return (
    <>
      <CurrencyVault
        header={{
          estimatedTotalLabel: formatWithCommas(summary?.estimatedTotalIqd ?? "0"),
          profitTotalLabel: plusSigned(totalProfit),
          top: summary?.profits.top ? { code: summary.profits.top.code, profitLabel: formatWithCommas(summary.profits.top.profitIqd) } : null,
          mainVaultLabel: formatWithCommas(summary?.iqdVault?.total ?? "0"),
        }}
        cards={visible.map((c) => ({ code: c.code, name: nameOf(c), symbol: c.symbol, balanceLabel: formatWithCommas(c.stock?.balance ?? "0"), equivalentLabel: formatWithCommas(c.estimatedIqd), suspended: c.suspended }))}
        activeCode={cur?.code ?? ""}
        onSelectCurrency={setActiveCode}
        selected={selected}
        canEditRates={hasPermission("settings_currencies")}
        onSaveRates={saveRates}
        editRatesInitial={{ buy: cur?.rates?.buy ?? "", sell: cur?.rates?.sell ?? "" }}
        form={form}
        onFormChange={patch}
        onOpTypeChange={onOpTypeChange}
        onCounterpartyChange={onCounterpartyChange}
        onSettlementChange={onSettlementChange}
        onAllocationChange={(a) => patch({ allocation: a, customerName: "", customerId: "" })}
        usdAvailable={usdAvailable}
        iqdVaultLabel={formatWithCommas(summary?.iqdVault?.total ?? "0")}
        usdVaultLabel={formatWithCommas(summary?.usdVault?.total ?? "0")}
        directTypes={directTypes}
        customerOptions={customerOptions}
        onCustomerPick={onCustomerPick}
        onCashReceivedChange={onCashReceivedChange}
        onRemainingChange={onRemainingChange}
        onDueDateShortcut={(d) => patch({ dueDate: addDays(today, d) })}
        showBillCounter={showBillCounter}
        onToggleBillCounter={() => setShowBillCounter((x) => !x)}
        billCounts={billCounts}
        onBillCountChange={onBillCountChange}
        preview={previewBox}
        stockShort={stockErr ? { requestedLabel: formatWithCommas(String(stockErr.params["requested"] ?? form.amount)), availableLabel: formatWithCommas(String(stockErr.params["available"] ?? "0")), canFill: positive(String(stockErr.params["available"] ?? "0")) } : null}
        onFillMax={() => stockErr && patch({ amount: String(stockErr.params["available"]) })}
        cashShort={cashErr ? { costLabel: formatWithCommas(String(cashErr.params["requested"] ?? "0")), availableLabel: formatWithCommas(String(cashErr.params["available"] ?? "0")) } : null}
        canSubmit={canSubmit && !busy}
        busy={busy}
        onSubmit={() => void submit()}
        rows={rows}
        search={search}
        onSearchChange={setSearch}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        currencyFilter={currencyFilter}
        currencyFilterOptions={all.map((c) => ({ code: c.code, label: `${nameOf(c)} (${c.code})` }))}
        onCurrencyFilterChange={setCurrencyFilter}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        onPrint={print}
        ranking={ranking}
      />
      {excess.dialog}
      {names.dialog}
      {gateway.pin.dialog}
      {printRows &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            <StatementA4
              locale={locale}
              title={t("cv.print_report_title")}
              customerName={t("cv.print_division")}
              dateLabel={today}
              docNumber={String(printRows[0]?.chainSeq ?? 0).padStart(6, "0")}
              office={printOffice}
              {...(startDate ? { periodFrom: startDate } : {})}
              {...(endDate ? { periodTo: endDate } : {})}
              // [C36] D09 T-17 / D-148 / D-337: the office's "hide profit" drops the marked profit column and total (the
              // template's rule, the same the server applies to a PDF)
              hideProfit={!printSettings.showProfit}
              columns={[
                { header: t("cv.print_col_date"), key: "date" },
                { header: t("cv.print_col_ref"), key: "ref" },
                { header: t("cv.print_col_currency"), key: "currency" },
                { header: t("cv.print_col_type"), key: "typeLabel" },
                { header: t("cv.print_col_amount"), key: "amount", numeric: true },
                { header: t("cv.print_col_rate"), key: "rate", numeric: true },
                { header: t("cv.print_col_equiv"), key: "equiv", numeric: true },
                { header: t("cv.print_col_profit"), key: "profit", numeric: true, profit: true },
                { header: t("cv.print_col_note"), key: "note" },
                { header: t("cv.print_col_user"), key: "user" },
              ]}
              rows={printRows.map((r) => {
                const c = byCode.get(r.currency);
                return {
                  date: fmtDateTime.format(new Date(r.postedAt)),
                  ref: String(r.chainSeq),
                  currency: `${c ? nameOf(c) : r.currency} (${r.currency})`,
                  typeLabel: t(`cv.print_type_${r.kind}`),
                  amount: `${formatWithCommas(r.qty)} ${c?.symbol ?? ""}`,
                  rate: r.rate ? `${formatWithCommas(r.rate)} ${r.settlement === "USD" ? "$" : t("cv.iqd")}` : "-",
                  equiv: r.equivalentIqd !== "0" ? `${formatWithCommas(r.equivalentIqd)} ${t("cv.iqd")}` : "-",
                  profit: r.kind === "sell" ? `${signed(r.profitIqd)} ${t("cv.iqd")}` : "-",
                  note: r.note || "-",
                  user: r.actorUsername ?? "-",
                };
              })}
              summary={[
                { label: t("cv.print_count"), value: String(printRows.length) },
                { label: t("cv.print_total_profit"), value: `${signed(sumProfit(printRows))} ${t("cv.iqd")}`, profit: true },
                { label: t("cv.print_liquidity"), value: `${formatWithCommas(summary?.estimatedTotalIqd ?? "0")} ${t("cv.iqd")}` },
              ]}
            />
          </div>,
          document.body,
        )}
    </>
  );
}

function D_SELL_LE_BUY(buy: string, sell: string): boolean {
  return toScaled(sell) <= toScaled(buy);
}
function sumProfit(rows: readonly CurrencyOperationRow[]): string {
  let s = 0n;
  for (const r of rows) if (r.kind === "sell") s += toScaled(r.profitIqd);
  return fromScaled(s);
}
